/**
 * MarketStatusUtils.ts
 * 
 * Utilities for determining start-of-year and end-of-year market status
 * for dual timing display in the Marktlage column.
 */

import { OneTopfYearData } from '../types';
import { ThreeTopfYearData } from '../types/simulation';

export type YearData = OneTopfYearData | ThreeTopfYearData;

/**
 * Determines the start-of-year market status based on crisis definition and available data
 * CORRECTED: Crisis status should carry over from previous year's end to current year's start
 */
export function getStartOfYearMarketStatus(
  currentYear: YearData,
  previousYear: YearData | undefined,
  simulationData: YearData[],
  crisisDefinition: 'negative-return' | 'kapitalindex-drawdown' = 'negative-return'
): 'Hoch' | 'Krise' {
  // BUG FIX v2.4.6: For the first year (no previous year), start-of-year is ALWAYS "Hoch"
  // There's no prior crisis history at the start of the simulation
  if (!previousYear) {
    return 'Hoch';
  }

  // Find the current year index in the simulation data
  const currentIndex = simulationData.findIndex(data => data.jahr === currentYear.jahr);
  
  if (crisisDefinition === 'negative-return') {
    // CORRECTED LOGIC: For negative return crisis, the start-of-year decision
    // should be based on the PREVIOUS year's end-of-year status.
    // At 01.01 of year N, you only know the results through 31.12 of year N-1.
    // Crisis status carries over until the current year proves recovery.
    return previousYear.marktSituation;
  } else {
    // For capital index drawdown crisis, decision is based on start-of-year information
    // We need to reconstruct what the crisis status would have been at the start of this year
    return reconstructStartOfYearCrisisStatus(
      currentIndex,
      simulationData,
      previousYear
    );
  }
}

/**
 * Reconstructs what the crisis status would have been at the start of the year
 * based on capital index drawdown logic
 */
function reconstructStartOfYearCrisisStatus(
  currentIndex: number,
  simulationData: YearData[],
  previousYear: YearData
): 'Hoch' | 'Krise' {
  // Get the start-of-year capital index (which is the previous year's end)
  const startOfYearIndex = previousYear.kapitalIndex;
  
  // Find the historical peak up to the previous year
  const historicalPeak = getHistoricalPeak(currentIndex - 1, simulationData);
  
  // Determine if we would have been in crisis at start of year
  // Crisis starts when capital index drops below any previous peak
  const wouldBeInCrisis = startOfYearIndex < historicalPeak;
  
  return wouldBeInCrisis ? 'Krise' : 'Hoch';
}

/**
 * Calculates the historical peak capital index up to a given year index
 */
function getHistoricalPeak(upToIndex: number, simulationData: YearData[]): number {
  let peak = 100; // Start with initial index of 100
  
  for (let i = 0; i <= upToIndex && i < simulationData.length; i++) {
    const yearData = simulationData[i];
    if (yearData.kapitalIndex > peak) {
      peak = yearData.kapitalIndex;
    }
  }
  
  return peak;
}

/**
 * Gets the end-of-year market status (this is just the stored marktSituation)
 */
export function getEndOfYearMarketStatus(yearData: YearData): 'Hoch' | 'Krise' {
  return yearData.marktSituation;
}

/**
 * Formats market status for display
 */
export function formatMarketStatus(status: 'Hoch' | 'Krise'): string {
  return status;
}

/**
 * Determines if there's a mismatch between start and end of year status
 */
export function hasMarketStatusMismatch(
  startOfYearStatus: 'Hoch' | 'Krise',
  endOfYearStatus: 'Hoch' | 'Krise'
): boolean {
  return startOfYearStatus !== endOfYearStatus;
}
